import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { isEmpty, omit } from 'lodash'
import { useMessagesStore } from '@/stores/messages'
import { stateFingerprint } from '@/utils/state_helpers'
import { useCurrentUserStore } from '@/stores/current_user'
import { useCustomFoldersStore } from '@/stores/custom_folders'
import { useExportFormatsStore } from '@/stores/export_formats'
import { useIssueStore } from '@/stores/issue'
import { useIssuesStore } from '@/stores/issues'
import { usePublicationsStore } from '@/stores/publications'
import { useSubsitesStore } from '@/stores/subsites'
import { useSectionsStore } from '@/stores/sections'
import { useUnrecognizedFilesStore } from '@/stores/unrecognized_files'
import { useUsersStore } from '@/stores/users'
import { useStoryPlacementsStore } from '@/stores/story_placements'
import { useSqueuesStore } from '@/stores/squeues'
import { useStoriesStore } from '@/stores/stories'
import { useWebCategoriesStore } from '@/stores/web_categories'
import { useStoryMediaStore } from '@/stores/story_media'
import { useStoryPublicationsStore } from '@/stores/story_publications'
import { usePullQuoteStore } from '@/stores/pull_quote'

const FINGERPRINT_OMITTED_FIELDS = ['cleanFingerprint', 'editingAttachments', 'locker', 'lock_expires_at', 'lock_version', 'client_lock_session_id']

export const useStoryStore = defineStore('story', {
  state: () => ({
    id: null,
    squeue_id: null,
    user_id: null,
    slug: null,
    keywords: null,
    headline: null,
    drop_head: null,
    web_head: null,
    overline: null,
    infobox_headline: null,
    byline: null,
    content: null,
    web_summary: null,
    web_slug: null,
    web_body: null,
    infobox_text: null,
    pub_date: null,
    featured: null,
    comments_disabled: null,
    ispublic: null,
    notes: null,
    credits: null,
    endnote: null,
    freelance: null,
    section_ids: [],
    web_category_ids: [],
    export_format_ids: [],
    content_path: null,
    open_folder_url: null,
    file_mode: 'per_field',
    twitter_feed: null,
    tweet: null,
    google_search_ad: null,
    outbrain_ad: null,
    saved: false,
    spiked: false,
    locker: { id: null, name: null },
    client_lock_session_id: null,
    lock_version: 0,
    lock_expires_at: null,
    updated_at: null,
    can_edit: true,
    editingAttachments: false,
    cleanFingerprint: null
  }),

  getters: {
    exceptFingerprintOmitted() {
      return omit({ ...this.$state }, FINGERPRINT_OMITTED_FIELDS)
    },

    isDirty() {
      return stateFingerprint(this.exceptFingerprintOmitted) !== this.cleanFingerprint
    },

    lockedToUser({ locker }) {
      const currentUserStore = useCurrentUserStore()
      return locker?.id && locker?.id !== currentUserStore.currentUser?.id
    },

    contentPath: ({ content_path }) => content_path,
    openFolderUrl: ({ open_folder_url }) => open_folder_url,
    updatedAt: ({ updated_at }) => updated_at,
    singleFileMode: ({ file_mode }) => file_mode === 'single',
    perFieldFileMode: ({ file_mode }) => file_mode === 'per_field'
  },

  actions: {
    async fetch({ id }) {
      // Clear stale locker only when switching stories. Clearing it for same-story
      // refreshes causes the lock overlay to flicker away before takeover finishes.
      if (this.id && Number(this.id) !== Number(id)) {
        this.locker = { id: null, name: null }
      }

      return httpClient.get(`/api/internal/stories/${id}`)
        .then(({ data: { story } }) => {
          Object.assign(this, story)
          this._resetDirty()
          return story
        })
        .catch(err => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    _resetDirty() {
      this.cleanFingerprint = stateFingerprint(omit({ ...this.$state }, FINGERPRINT_OMITTED_FIELDS))
    },

    setClientLockSessionId(lockSessionId) {
      this.client_lock_session_id = lockSessionId || null
    },

    async fetchDependentData() {
      const storyId = this.id
      const currentUserStore = useCurrentUserStore()
      const customFoldersStore = useCustomFoldersStore()
      const exportFormatsStore = useExportFormatsStore()
      const unrecognizedFilesStore = useUnrecognizedFilesStore()
      const publicationsStore = usePublicationsStore()
      const subsitesStore = useSubsitesStore()
      const usersStore = useUsersStore()
      const webCategoriesStore = useWebCategoriesStore()
      const sectionsStore = useSectionsStore()
      const squeuesStore = useSqueuesStore()

      return Promise.all([
        currentUserStore.fetchOnce(),
        customFoldersStore.fetchForStory(this.id),
        useStoryMediaStore().fetchForStory(this.id),
        unrecognizedFilesStore.fetchForStory(this.id),
        useStoryPublicationsStore().fetch({ storyId }),
        useStoryPlacementsStore().fetch({ storyId }),
        usePullQuoteStore().fetchForStory({ storyId }),
        exportFormatsStore.fetchOnce(),
        publicationsStore.fetchOnce(),
        sectionsStore.fetchOnce(),
        squeuesStore.fetchOnce(),
        usersStore.fetchOnce(),
        webCategoriesStore.fetchOnce(),
        subsitesStore.fetchOnce()
      ])
    },

    async create({ issueId, pageId, path, filename, file }) {
      let formData = new FormData()
      if (issueId) formData.append('issue_id', issueId)
      if (pageId) formData.append('page_id', pageId)
      if (path) formData.append('path', path)
      if (filename) formData.append('filename', filename)
      if (file) formData.append('file', file)

      if (formData.entries().next().done === true) {
        formData = null
      }

      return httpClient.post('/api/internal/stories/', formData)
        .then(({ data }) => {
          this.id = data.story.id
          if (data.flash) {
            useMessagesStore().addFlash(data.flash)
          }
          return data.story
        })
        .catch(err => {
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    async save() {
      const formData = this._toFormData()
      // Include lock_version for optimistic locking check
      formData.append('lock_version', this.lock_version)
      if (this.client_lock_session_id) {
        formData.append('lock_session_id', this.client_lock_session_id)
      }
      
      return httpClient.patch(`/api/internal/stories/${this.id}`, formData)
        .then(({ data }) => {
          Object.assign(this, data.story)
          this._resetDirty()
          this.fetchDependentData()
          if (data.flash) {
            useMessagesStore().addFlash(data.flash)
            return { hasFlash: true }
          }
          return { hasFlash: false }
        })
        .catch(err => {
          // Handle version conflict (409)
          if (err.response?.status === 409) {
            const messagesStore = useMessagesStore()
            const message = err.response.data?.error || 'This story changed in another editing session. Refresh to continue.'
            console.error('[StoryLock] Version conflict on story save', {
              storyId: this.id,
              clientLockVersion: this.lock_version,
              clientLockSessionId: this.client_lock_session_id,
              response: err.response?.data,
              error: err.message
            })
            messagesStore.addError(message)
            // Throw a specific error type for the UI to handle
            const conflictError = new Error(message)
            conflictError.status = 409
            throw conflictError
          }
          useMessagesStore().smartAdd(err)
          throw err
        })
    },

    async spike(storyId) {
      const id = storyId ?? this.id
      return httpClient.delete(`/api/internal/stories/${id}`)
        .then((response) => {
          this.$reset()
          return response
        })
    },

    async unlock() {
      return httpClient.delete(`/api/internal/stories/${this.id}/lock`, {
        params: { lock_session_id: this.client_lock_session_id }
      })
        .then(({ data: { locker } }) => {
          this.locker = locker
          this.lock_expires_at = null

          const storiesStore = useStoriesStore()
          storiesStore.updateStory({
            story: {
              id: this.id,
              locker,
              lock_expires_at: null
            }
          })
        })
    },

    async heartbeat() {
      // Extend lock TTL by sending heartbeat to server
      if (!this.id || !this.locker?.id || !this.client_lock_session_id) return

      return httpClient.post(`/api/internal/stories/${this.id}/lock/heartbeat`, {
        lock_session_id: this.client_lock_session_id
      })
        .then(({ data }) => {
          this.lock_expires_at = data.lock_expires_at
          return data
        })
        .catch(err => {
          // Silently fail heartbeat; not a critical operation
          console.warn('[StoryLock] Heartbeat failed:', err.message)
        })
    },

    unlockOnLeave() {
      const csrfMetaTag = document.getElementsByName('csrf-token')[0]
      if (!csrfMetaTag) return

      const csrfToken = csrfMetaTag.getAttribute('content')
      const data = new FormData()
      data.append('_method', 'delete')
      data.append('authenticity_token', csrfToken)
      if (this.client_lock_session_id) {
        data.append('lock_session_id', this.client_lock_session_id)
      }
      navigator.sendBeacon(`/api/internal/stories/${this.id}/lock`, data)
    },

    async overrideLock() {
      return httpClient.post(`/api/internal/stories/${this.id}/lock`, {
        lock_session_id: this.client_lock_session_id
      })
        .then(({ data: { locker, lock_session_id } }) => {
          this.locker = locker
          if (lock_session_id) {
            this.client_lock_session_id = lock_session_id
          }
        })
    },

    async autoSetPubDateFromIssue({ issueId }) {
      if (!issueId) return

      const issuesStore = useIssuesStore()
      const issueStore = useIssueStore()
      let issue = issuesStore.forId(issueId)

      if (!issue) {
        issue = await issueStore.fetch({ issueId })
      }

      this.pub_date = issue.publish_on || null
    },

    _toFormData() {
      let formData = new FormData()

      const currentUserStore = useCurrentUserStore()
      const editableFields = currentUserStore.currentUser?.editable_story_fields || []
      const canEditField = (fieldName) => editableFields.includes(fieldName)

      const simpleParams = [
        'user_id', 'squeue_id', 'slug', 'keywords', 'headline',
        'drop_head', 'web_head', 'overline', 'infobox_headline', 'byline', 'content',
        'web_summary', 'infobox_text', 'pub_date', 'featured', 'comments_disabled',
        'freelance', 'ispublic', 'notes', 'credits', 'endnote', 'file_mode', 'twitter_feed',
        'tweet', 'google_search_ad', 'outbrain_ad', 'web_body', 'web_slug'
      ]

      simpleParams.filter(p => canEditField(p)).forEach(param => {
        if (Object.prototype.hasOwnProperty.call(this.$state, param)) {
          let value = this[param]
          if (value === null || value === undefined) { value = '' }
          formData.append(`story[${param}]`, value)
        }
      })

      if (canEditField('web_category_ids')) {
        this.web_category_ids.forEach(id => {
          formData.append('story[web_category_ids][]', id)
        })
        if (isEmpty(this.web_category_ids)) {
          formData.append('story[web_category_ids][]', '')
        }
      }

      if (canEditField('section_ids')) {
        this.section_ids.forEach(id => {
          formData.append('story[section_ids][]', id)
        })
        if (isEmpty(this.section_ids)) {
          formData.append('story[section_ids][]', '')
        }
      }

      if (canEditField('export_format_ids')) {
        this.export_format_ids.forEach(id => {
          formData.append('story[export_format_ids][]', id)
        })
        if (isEmpty(this.export_format_ids)) {
          formData.append('story[export_format_ids][]', '')
        }
      }

      if (canEditField('pull_quote_attributes')) {
        const { headline, quote, attribution } = usePullQuoteStore()
        formData.append('story[pull_quote_attributes][headline]', headline)
        formData.append('story[pull_quote_attributes][quote]', quote)
        formData.append('story[pull_quote_attributes][attribution]', attribution)
      }

      if (canEditField('story_placements_attributes')) {
        const storyPlacementsStore = useStoryPlacementsStore()
        storyPlacementsStore.storyPlacements.forEach((placement, i) => {
          const { issue_id, page_id, _destroy } = placement
          if (!issue_id && !page_id && !_destroy) return

          ;['id', 'issue_id', 'page_id', '_destroy'].forEach(param => {
            formData.append(
              `story[story_placements_attributes][${i}][${param}]`,
              placement[param] || ''
            )
          })
        })
      }

      return formData
    }
  }
})
